import { FC, ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { PaymentDialog } from './PaymentDialog';

type TPaymentViewer = {
	/** Opens the payment card from any tab (alerts, customers, refunds, outbox…). */
	openPayment: (transactionId: string) => void;
	/** Bumps when a payment was changed from the card: lists should re-read. */
	changeCounter: number;
};

const PaymentViewerContext = createContext<TPaymentViewer>({
	openPayment: () => undefined,
	changeCounter: 0,
});

/** One payment card for the whole Payment Gateway section. */
export const PaymentViewerProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [transactionId, setTransactionId] = useState<string | null>(null);
	const [changeCounter, setChangeCounter] = useState(0);

	const openPayment = useCallback((id: string) => setTransactionId(id), []);
	const value = useMemo(() => ({ openPayment, changeCounter }), [openPayment, changeCounter]);

	return (
		<PaymentViewerContext.Provider value={value}>
			{children}
			<PaymentDialog
				transactionId={transactionId}
				onClose={() => setTransactionId(null)}
				onChanged={() => setChangeCounter((counter) => counter + 1)}
			/>
		</PaymentViewerContext.Provider>
	);
};

export const usePaymentViewer = (): TPaymentViewer => useContext(PaymentViewerContext);
